import { API } from "aws-amplify";
import Auth from "@aws-amplify/auth";
import { store } from "../../store";

const getHeaders = async () => {
  const user = await Auth.currentAuthenticatedUser();
  const jwtToken = user.signInUserSession?.accessToken?.jwtToken;
  const headers = {};
  if (jwtToken) headers['Authorization'] = 'Bearer ' + jwtToken;
  const { loginReducer } = store.getState();
  const clientId = loginReducer?.groups?.[0];
  if (clientId) headers['BluStream-Client-ID'] = clientId;
  return headers;
};

const customerUuid = () => {
  const { loginReducer } = store.getState();
  return loginReducer?.groups?.[0];
};

// GET /api/documentation_manager/customer/{customerUuid}/documentation
// Returns ClientDocumentationPage { content, page, size, totalElements, totalPages }
export const getDocuments = async ({ page = 0, size = 10, type, status, search } = {}) => {
  const uuid = customerUuid();
  const headers = await getHeaders();
  const params = new URLSearchParams();
  params.set('page', page);
  params.set('size', size);
  if (type && type !== 'all') params.set('type', type);
  if (status) params.set('status', status);
  if (search) params.set('search', search);
  return API.get("blustreamREST", `/gandalfproxy/api/documentation_manager/customer/${uuid}/documentation?${params.toString()}`, { headers });
};

// POST /api/documentation_manager/customer/{customerUuid}/documentation (multipart)
// files part: one or more file uploads
// storeInputs part: one entry per file. The backend builds documents by iterating
// storeInputs and matching each to its file by content == originalFilename, so a
// storeInputs entry with content set to the file name is required or nothing is ingested.
export const uploadDocumentFiles = async (files) => {
  const uuid = customerUuid();
  const headers = await getHeaders();
  const formData = new FormData();
  files.forEach((file) => formData.append('files', file));
  const storeInputs = JSON.stringify(files.map((file) => ({ name: file.name, content: file.name })));
  formData.append('storeInputs', new Blob([storeInputs], { type: 'application/json' }));
  return API.post("blustreamREST", `/gandalfproxy/api/documentation_manager/customer/${uuid}/documentation`, {
    headers,
    body: formData,
  });
};

// POST /api/documentation_manager/customer/{customerUuid}/documentation (multipart)
// storeInputs part: JSON array [{ name, content }] for raw text ingestion
export const addDocumentText = async (title, content) => {
  const uuid = customerUuid();
  const headers = await getHeaders();
  const storeInputs = JSON.stringify([{ name: title, content }]);
  const formData = new FormData();
  formData.append('storeInputs', new Blob([storeInputs], { type: 'application/json' }));
  return API.post("blustreamREST", `/gandalfproxy/api/documentation_manager/customer/${uuid}/documentation`, {
    headers,
    body: formData,
  });
};

// POST /api/documentation_manager/customer/{customerUuid}/documentation (multipart)
// storeInputs part: [{ name, content }] where content is an absolute URL.
// Gandalf's DocumentationManager.prepareRawDocuments() detects isAbsolute() and routes through
// WebDocumentFetchStrategy (web page) or YouTubeInMemoryRawDocumentFetchStrategy (YouTube URLs).
// GET /api/documentation_manager/customer/{customerUuid}/documentation/{docId}/history
export const getIngestionHistory = async (normalizedDocumentId) => {
  const uuid = customerUuid();
  const headers = await getHeaders();
  return API.get("blustreamREST", `/gandalfproxy/api/documentation_manager/customer/${uuid}/documentation/${normalizedDocumentId}/history`, { headers });
};

// POST /api/documentation_manager/customer/{customerUuid}/documentation/{docId}/refresh
export const refreshDocument = async (normalizedDocumentId) => {
  const uuid = customerUuid();
  const headers = await getHeaders();
  return API.post("blustreamREST", `/gandalfproxy/api/documentation_manager/customer/${uuid}/documentation/${normalizedDocumentId}/refresh`, { headers });
};

// POST /api/documentation_manager/customer/{customerUuid}/documentation/{docId}/archive
export const archiveDocument = async (normalizedDocumentId) => {
  const uuid = customerUuid();
  const headers = await getHeaders();
  return API.post("blustreamREST", `/gandalfproxy/api/documentation_manager/customer/${uuid}/documentation/${normalizedDocumentId}/archive`, { headers });
};

// POST /api/documentation_manager/customer/{customerUuid}/documentation/{docId}/restore
export const restoreDocument = async (normalizedDocumentId) => {
  const uuid = customerUuid();
  const headers = await getHeaders();
  return API.post("blustreamREST", `/gandalfproxy/api/documentation_manager/customer/${uuid}/documentation/${normalizedDocumentId}/restore`, { headers });
};

export const addDocumentUrl = async (url) => {
  const uuid = customerUuid();
  const headers = await getHeaders();
  const name = url.replace(/^https?:\/\//, '').replace(/\/$/, '');
  const storeInputs = JSON.stringify([{ name, content: url }]);
  const formData = new FormData();
  formData.append('storeInputs', new Blob([storeInputs], { type: 'application/json' }));
  return API.post("blustreamREST", `/gandalfproxy/api/documentation_manager/customer/${uuid}/documentation`, {
    headers,
    body: formData,
  });
};
